/*----------------------------------
- HUMAN PAGE RELATIONSHIPS (SSOT)
----------------------------------*/

// Cross-runtime relationship registry for contextual links between canonical
// public pages. Labels describe the destination value so they stay useful in
// visible navigation, Markdown mirrors, and AI-readable discovery resources.

import { FEATURED_PUBLIC_SEARCH_RESOURCES } from '@uniquedomains/core/domains/public-search';
import { parseTldInsightPath } from '@uniquedomains/core/domains/tlds';
import { DEALFLOW_COPY } from '@uniquedomains/components/contracts/opportunities/dealflowCopy';

/*----------------------------------
- TYPES
----------------------------------*/

export type HumanPageRelationshipSourcePath =
    | '/product/search'
    | '/product/appraise'
    | '/product/verify'
    | '/trending-domains'
    | '/business-ideas'
    | '/product/radars'
    | '/product/watchlists'
    | '/solutions/investors'
    | '/solutions/founders'
    | '/solutions/studios'
    | '/api'
    | '/team'
    | '/newsletter'
    | '/agenda'
    | '/tld'
    | '/tld/applications'
    | '/sales'
    | '/auctions'
    | '/registrars'
    | '/whois'
    | '/availability'
    | '/glossary'
    | '/compare/one-word-domains'
    | '/compare/atom'
    | '/compare/bishopi'
    | '/compare/mononames'
    | '/compare/dotdb'
    | '/compare/namebio'
    | '/compare/estibot'
    | '/compare/instant-domain-search'
    | '/compare/expireddomains-net'
    | '/compare/brandbucket';

export type HumanPageRelationshipTargetPath =
    | HumanPageRelationshipSourcePath
    | '/compare'
    | '/resources/domaining-news'
    | '/domains'
    | '/domains/brandable'
    | '/domains/expiring';

export type HumanPageRelationship = {
    label: string;
    targetPath: HumanPageRelationshipTargetPath;
};

/*----------------------------------
- CATALOG
----------------------------------*/

export const HUMAN_PAGE_RELATIONSHIP_REGISTRY = {
    '/product/search': [
        {
            label: 'Verify price, history, and risk before buying',
            targetPath: '/product/verify',
        },
        {
            label: 'Save a repeatable search with Radars',
            targetPath: '/product/radars',
        },
        {
            label: 'Track shortlisted domains with Watchlists',
            targetPath: '/product/watchlists',
        },
    ],
    '/product/verify': [
        {
            label: 'Search one-word domains by prompt, keyword, or filters',
            targetPath: '/product/search',
        },
        {
            label: 'Appraise the name through every source at once',
            targetPath: '/product/appraise',
        },
        {
            label: 'Compare Unique Domains with alternative domain tools',
            targetPath: '/compare',
        },
    ],
    /* Product pages carry three edges, one per column of the related-links grid. Each
       persona that reads the feed gets its own way out (founders see it as Business Ideas),
       and the third is Watchlists, the feed's own next step (quick-add). Verify gave up its
       place on 2026-09-24: it is the best-linked page on the site, with eleven inbound edges. */
    // The daily editions (ADR-0074): the other persona's page, the trend's next step (a Radar) and
    // the weekly email that links the day's edition.
    '/trending-domains': [
        {
            label: 'Read the same edition as Business Ideas, with three ideas per name',
            targetPath: '/business-ideas',
        },
        {
            label: 'Follow a trend with a Radar',
            targetPath: '/product/radars',
        },
        {
            label: 'Get the weekly email of the editions',
            targetPath: '/newsletter',
        },
    ],
    '/business-ideas': [
        {
            label: 'Read the same edition as Trending Domains, with a verdict for investors',
            targetPath: '/trending-domains',
        },
        {
            label: 'See the founder workflow from brief to shortlist',
            targetPath: '/solutions/founders',
        },
        {
            label: 'Get the weekly email of the editions',
            targetPath: '/newsletter',
        },
    ],
    '/product/radars': [
        {
            label: 'Build a one-word domain search',
            targetPath: '/product/search',
        },
        {
            label: 'Read today’s edition of trending one-word domains',
            targetPath: '/trending-domains',
        },
        {
            label: 'Track Radar matches with Watchlists',
            targetPath: '/product/watchlists',
        },
    ],
    '/product/appraise': [
        {
            label: 'Verify price, history, and risk before buying',
            targetPath: '/product/verify',
        },
        {
            label: 'Track appraised candidates with Watchlists',
            targetPath: '/product/watchlists',
        },
        {
            label: 'Compare Unique Domains with Estibot',
            targetPath: '/compare/estibot',
        },
    ],
    '/product/watchlists': [
        {
            label: 'Search for more one-word domain candidates',
            targetPath: '/product/search',
        },
        {
            label: 'Read today’s edition of trending one-word domains',
            targetPath: '/trending-domains',
        },
        {
            label: 'Appraise a shortlist through every source at once',
            targetPath: '/product/appraise',
        },
    ],
    '/solutions/investors': [
        {
            label: 'See the founder workflow from brief to shortlist',
            targetPath: '/solutions/founders',
        },
        {
            label: 'See the studio and agency workflow for client shortlists',
            targetPath: '/solutions/studios',
        },
        {
            label: 'Browse expiring one-word domains',
            targetPath: '/domains/expiring',
        },
    ],
    '/solutions/founders': [
        {
            label: 'See the investor workflow for a repeatable stream of new domains',
            targetPath: '/solutions/investors',
        },
        {
            label: 'See the studio and agency workflow for client shortlists',
            targetPath: '/solutions/studios',
        },
        {
            label: 'Browse brandable one-word domains',
            targetPath: '/domains/brandable',
        },
    ],
    '/solutions/studios': [
        {
            label: 'See the investor workflow for a repeatable stream of new domains',
            targetPath: '/solutions/investors',
        },
        {
            label: 'See the founder workflow from brief to shortlist',
            targetPath: '/solutions/founders',
        },
        {
            label: 'Read the Unique Domains API documentation',
            targetPath: '/api',
        },
    ],
    '/api': [
        {
            label: 'Search one-word domains by prompt, keyword, or filters',
            targetPath: '/product/search',
        },
        {
            label: 'Explore the public one-word domain Screener',
            targetPath: '/domains',
        },
        {
            label: 'Read what every database column means',
            targetPath: '/glossary',
        },
    ],
    '/team': [
        {
            label: 'See how one-word domain search works',
            targetPath: '/product/search',
        },
        {
            label: 'See how Unique Domains verifies price, history, and risk',
            targetPath: '/product/verify',
        },
        {
            label: 'Read the Unique Domains API documentation',
            targetPath: '/api',
        },
    ],
    // The glossary's first edge pointed at the data page, removed on 2026-10-04. Its third edge
    // since 2026-10-05 is the sales page, where the glossary's sale and price terms are read live.
    // Its second edge since 2026-10-09 is the WHOIS lookup (ADR-0078), where the status-code and
    // lifecycle terms (redemption, pending delete, transfer lock) are read on a live record.
    '/glossary': [
        {
            label: 'Explore the public one-word domain Screener',
            targetPath: '/domains',
        },
        {
            label: 'Look up a name\u2019s registry record, status codes and drop date',
            targetPath: '/whois',
        },
        {
            label: 'See the newest reported domain sales and their prices',
            targetPath: '/sales',
        },
    ],
    '/newsletter': [
        {
            label: 'Read every published Domaining News edition',
            targetPath: '/resources/domaining-news',
        },
        {
            label: 'Read today’s edition of trending one-word domains',
            targetPath: '/trending-domains',
        },
        {
            label: 'Explore the public one-word domain Screener',
            targetPath: '/domains',
        },
    ],
    /* The agenda and the applications list lead to each other (launch dates and the strings
       that will launch), to the Radar that follows a launch, and to the Wednesday email that
       carries the agenda. */
    '/agenda': [
        {
            label: 'See every application in the 2026 new gTLD round',
            targetPath: '/tld/applications',
        },
        {
            label: 'Follow a new TLD to launch day with a Radar',
            targetPath: '/product/radars',
        },
        {
            label: 'Get the agenda by email every Wednesday',
            targetPath: '/newsletter',
        },
    ],
    /* The hub leads to the two other TLD surfaces, the launch calendar and the applied-for
       strings, and to the sales page, whose ending ladder carries the 12-month sales the board
       shows per TLD (ADR-0067; it replaced the Radar edge, which /tld/applications and every
       product page already carry). Each insight page links the hub back from its own related
       links (built per TLD, so not in this static registry). */
    '/tld': [
        {
            label: 'See TLD launch dates and registry changes on the agenda',
            targetPath: '/agenda',
        },
        // The registrar hub (ADR-0079) took the applications edge: the agenda still leads there.
        {
            label: 'Compare registrar prices for every ending',
            targetPath: '/registrars',
        },
        {
            label: 'See what names sold for on each ending, updated each night',
            targetPath: '/sales',
        },
    ],
    /* The sales page (ADR-0067) leads to what a member does with a price: compare TLDs, value a
       name, follow a word with a Radar. */
    '/sales': [
        {
            label: 'Compare TLDs on registry facts, prices and 12-month sales',
            targetPath: '/tld',
        },
        {
            label: 'Get an estimated value for the names you hold',
            targetPath: '/product/appraise',
        },
        {
            label: 'Follow a word to its next sale with a Radar',
            targetPath: '/product/radars',
        },
    ],
    /* The auctions page (ADR-0076) leads to what a closing bid is weighed against: what names sold
       for, the TLD's figures, and the Radar that catches the next auction of a word. */
    '/auctions': [
        {
            label: 'See what names sold for on each ending',
            targetPath: '/sales',
        },
        {
            label: 'Compare TLDs on registry facts, prices and 12-month sales',
            targetPath: '/tld',
        },
        {
            label: 'Catch the next auction of a word with a Radar',
            targetPath: '/product/radars',
        },
    ],
    /* The registrar hub (ADR-0079) leads to the ending's own figures, to the checker that prices one
       name, and to the Watchlist that holds the names a member keeps an eye on. */
    '/registrars': [
        {
            label: 'Compare TLDs on registry facts, prices and 12-month sales',
            targetPath: '/tld',
        },
        {
            label: 'Check a name and what it costs to hold',
            targetPath: '/availability',
        },
        {
            label: 'Keep an eye on names with a Watchlist',
            targetPath: '/product/watchlists',
        },
    ],
    /* The lookup tools (ADR-0078) lead to each other (the record, then what the name costs to hold)
       and, for an expired name, to the Watchlist that waits for the drop; WHOIS leads to the
       registrar prices of the ending, the checker to what a word sold for and to the names free
       to register. */
    '/whois': [
        {
            label: 'Check if a name is free and what it costs to hold',
            targetPath: '/availability',
        },
        /* The registrar hub (ADR-0079) took the `/tld` edge on 2026-10-11: a WHOIS record names the
           registrar, so its prices are the next question; `/tld` keeps inbound edges from most
           pages. Not `/availability`'s `/domains` edge: that page carries most signup journeys
           (founder decision of 2026-10-11, review R-68). */
        {
            label: 'Compare what each registrar charges for the ending',
            targetPath: '/registrars',
        },
        {
            label: 'Watch an expiring name until it drops with a Watchlist',
            targetPath: '/product/watchlists',
        },
    ],
    '/availability': [
        {
            label: 'Look up who holds a name and when it drops',
            targetPath: '/whois',
        },
        {
            label: 'See what names sold for on each ending',
            targetPath: '/sales',
        },
        {
            label: 'Browse one-word domains free to register',
            targetPath: '/domains',
        },
    ],
    '/tld/applications': [
        {
            label: 'See TLD launch dates and registry changes on the agenda',
            targetPath: '/agenda',
        },
        {
            label: 'Follow a new TLD to launch day with a Radar',
            targetPath: '/product/radars',
        },
        {
            label: 'Get launch dates by email every Wednesday',
            targetPath: '/newsletter',
        },
    ],
    '/compare/one-word-domains': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Search one-word domains by prompt, keyword, or filters',
            targetPath: '/product/search',
        },
        {
            label: 'See the investor workflow for a repeatable stream of new domains',
            targetPath: '/solutions/investors',
        },
    ],
    '/compare/atom': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Verify domain price, history, and risk before buying',
            targetPath: '/product/verify',
        },
        {
            label: 'See the founder workflow from brief to shortlist',
            targetPath: '/solutions/founders',
        },
    ],
    '/compare/bishopi': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Verify domain price, history, and risk before buying',
            targetPath: '/product/verify',
        },
        {
            label: 'See the investor workflow for a repeatable stream of new domains',
            targetPath: '/solutions/investors',
        },
    ],
    '/compare/mononames': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Search one-word domains by prompt, keyword, or filters',
            targetPath: '/product/search',
        },
        {
            label: 'See the founder workflow from brief to shortlist',
            targetPath: '/solutions/founders',
        },
    ],
    '/compare/dotdb': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Verify domain price, history, and risk before buying',
            targetPath: '/product/verify',
        },
        {
            label: 'See the investor workflow for a repeatable stream of new domains',
            targetPath: '/solutions/investors',
        },
    ],
    /* A reader comparing sales archives lands on the sales page (ADR-0067); it replaced the Verify
       edge, the best-linked page on the site. */
    '/compare/namebio': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Read the newest reported sales and the medians by ending',
            targetPath: '/sales',
        },
        {
            label: 'See the investor workflow for a repeatable stream of new domains',
            targetPath: '/solutions/investors',
        },
    ],
    '/compare/estibot': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Verify domain price, history, and risk before buying',
            targetPath: '/product/verify',
        },
        {
            label: 'See the investor workflow for a repeatable stream of new domains',
            targetPath: '/solutions/investors',
        },
    ],
    /* The availability checker replaced the search edge on 2026-10-09 (ADR-0078): a reader comparing
       instant availability tools lands on the free checker. */
    '/compare/instant-domain-search': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Check if a name is free and what it costs to hold',
            targetPath: '/availability',
        },
        {
            label: 'See the founder workflow from brief to shortlist',
            targetPath: '/solutions/founders',
        },
    ],
    '/compare/expireddomains-net': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Read today’s edition of trending one-word domains',
            targetPath: '/trending-domains',
        },
        {
            label: 'See the investor workflow for a repeatable stream of new domains',
            targetPath: '/solutions/investors',
        },
    ],
    '/compare/brandbucket': [
        {
            label: 'Browse all Unique Domains comparisons',
            targetPath: '/compare',
        },
        {
            label: 'Verify domain price, history, and risk before buying',
            targetPath: '/product/verify',
        },
        {
            label: 'See the founder workflow from brief to shortlist',
            targetPath: '/solutions/founders',
        },
    ],
} as const satisfies Readonly<Record<HumanPageRelationshipSourcePath, readonly HumanPageRelationship[]>>;

const EMPTY_HUMAN_PAGE_RELATIONSHIPS: readonly HumanPageRelationship[] = [];
const HOME_PAGE_DOMAIN_LINK_TARGETS: readonly HumanPageRelationshipTargetPath[] = ['/domains/brandable'];
export const GLOBAL_DOMAIN_NAVIGATION_TARGET_PATHS: readonly string[] = [
    '/domains',
    '/domains/brandable',
    '/domains/short',
    '/domains/positive',
    '/domains/hacks',
    '/domains/actionable',
    '/domains/expiring',
    '/domains/expired',
    ...FEATURED_PUBLIC_SEARCH_RESOURCES.map((resource) => resource.humanPath),
];
const GLOBAL_DOMAIN_NAVIGATION_TARGET_PATH_SET = new Set(GLOBAL_DOMAIN_NAVIGATION_TARGET_PATHS);

const normalizePublicPath = (path: string): string => {
    const pathname = path.split(/[?#]/, 1)[0] || '/';
    return pathname.replace(/\/+$/, '') || '/';
};

/*----------------------------------
- HELPERS
----------------------------------*/

export const getHumanPageRelationships = (canonicalPath: string): readonly HumanPageRelationship[] =>
    HUMAN_PAGE_RELATIONSHIP_REGISTRY[normalizePublicPath(canonicalPath) as HumanPageRelationshipSourcePath] ||
    EMPTY_HUMAN_PAGE_RELATIONSHIPS;

/**
 * A `/domains` listing or a TLD insight page, `/tld/<slug>` (ADR-0054, `parseTldInsightPath`):
 * both are published by the static warmup, so both are gated on publication. The hub (`/tld`)
 * and the applications list are ordinary human pages. Path-shaped on purpose: the warmup also
 * reads raw static cache keys, which carry no resource kind.
 */
export const isDomainInventoryHumanPath = (path: string): boolean => {
    const normalizedPath = normalizePublicPath(path);
    return normalizedPath === '/domains' || normalizedPath.startsWith('/domains/') || parseTldInsightPath(normalizedPath) !== null;
};

/**
 * Every published TLD insight page counts as a global navigation target, not only the six of
 * the seed: the header's ending column lists the public set up to its seven rows (D3), and an
 * admin pin has to be able to reach it. A change in that set republishes the static chrome like any other global target,
 * which happens at most a few times a month (admin pins and blocks only since ADR-0064 retired
 * the automatic entry).
 */
export const isGlobalDomainNavigationTargetPath = (path: string): boolean => {
    const normalizedPath = normalizePublicPath(path);
    return GLOBAL_DOMAIN_NAVIGATION_TARGET_PATH_SET.has(normalizedPath) || parseTldInsightPath(normalizedPath) !== null;
};

export const isPublishedHumanPageTarget = (
    targetPath: string,
    publishedDomainPaths?: ReadonlySet<string> | readonly string[],
): boolean => {
    if (!isDomainInventoryHumanPath(targetPath) || publishedDomainPaths === undefined) return true;

    const normalizedPublishedPaths = new Set(
        Array.from(publishedDomainPaths, (path) => normalizePublicPath(path)),
    );
    return normalizedPublishedPaths.has(normalizePublicPath(targetPath));
};

export const getPublishedHumanPageRelationships = (
    canonicalPath: string,
    publishedDomainPaths?: ReadonlySet<string> | readonly string[],
): readonly HumanPageRelationship[] => {
    return getHumanPageRelationships(canonicalPath).filter((relationship) =>
        isPublishedHumanPageTarget(relationship.targetPath, publishedDomainPaths),
    );
};

export const getHumanPageDomainLinkTargets = (
    canonicalPath: string,
): readonly HumanPageRelationshipTargetPath[] => {
    const normalizedPath = normalizePublicPath(canonicalPath);
    const relationshipTargets = getHumanPageRelationships(normalizedPath)
        .map((relationship) => relationship.targetPath)
        .filter(isDomainInventoryHumanPath);

    return normalizedPath === '/'
        ? [...HOME_PAGE_DOMAIN_LINK_TARGETS, ...relationshipTargets]
        : relationshipTargets;
};
